import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { useEditorStore } from 'CanvasCore/uiSlice'
import { useUI } from 'Editor'
import { useCallback, useEffect, useRef, type FC } from 'react'
import { useParams } from 'react-router-dom'
import type { Toning } from './types'

interface ToningPickerProps {
  tonings?: Toning[]
}

export const ToningPicker: FC<ToningPickerProps> = ({ tonings }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const toningRefs = useRef<Record<string, HTMLDivElement>>({})
  const toningId = useUI((state) => state.toningId)

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -200, behavior: 'smooth' })
  }

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' })
  }

  const scrollToToning = useCallback((id: string) => {
    const element = toningRefs.current[id]
    if (!element || !scrollRef.current) return

    const elementRect = element.getBoundingClientRect()
    const scrollContainerRect = scrollRef.current.getBoundingClientRect()
    const scrollOffset =
      elementRect.left -
      scrollContainerRect.left -
      scrollContainerRect.width / 2 +
      elementRect.width / 2

    scrollRef.current.scrollBy({ left: scrollOffset, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    if (toningId) {
      scrollToToning(toningId)
    }
  }, [toningId, scrollToToning])

  const setToningRef = useCallback((element: HTMLDivElement | null, id: string) => {
    if (element) {
      toningRefs.current[id] = element
    }
  }, [])

  return (
    <div className="flex items-center">
      <button type="button" onClick={scrollLeft} className="shrink-0">
        <ChevronLeftIcon className="w-8 h-8 text-gray-400 hover:text-primary" />
      </button>
      <div
        className="flex overflow-x-scroll w-full gap-4 scrollbar-hide"
        ref={scrollRef}
      >
        {tonings?.map((toning) => (
          <ToningItem
            key={toning.id}
            toning={toning}
            setRef={(el) => setToningRef(el, toning.id)}
          />
        ))}
      </div>
      <button type="button" className="ml-auto shrink-0" onClick={scrollRight}>
        <ChevronRightIcon className="w-8 h-8 text-gray-400 hover:text-primary" />
      </button>
    </div>
  )
}

interface ToningItemProps {
  toning: Toning
  setRef: (el: HTMLDivElement | null) => void
}

const ToningItem: FC<ToningItemProps> = ({ toning, setRef }) => {
  const { tid } = useParams<{ tid: string }>()
  const selected = useUI((state) => state.toningId === toning.id)
  const toggleToning = useUI((state) => state.toggleToning)

  const inTonings = useEditorStore(
    (state) => state.editors[tid!]?.tonings?.[toning.id],
  )

  return (
    <div
      className="flex flex-col w-full py-1 space-y-1"
      ref={setRef}
      onClick={() => toggleToning(toning.id)}
    >
      <Button
        variant="outline"
        className={cn(selected ? 'border border-primary' : '')}
      >
        {toning.name}
      </Button>
      <div
        className={cn(
          'w-full h-0.5 bg-primary rounded',
          inTonings ? '' : 'invisible',
        )}
      />
    </div>
  )
}
